// ForecastPanel — tiles + vista por etapa / por mes de cierre.
// Todos los importes vienen calculados por el servidor (GET /admin/deals/forecast);
// aquí solo se formatean y se dibujan barras proporcionales (misma gramática
// que «Resultados de campaña»: fila con etiqueta + valor + barra delgada).

import { useState } from 'react';
import { CalendarRange, Layers, Scale, Target, TrendingUp } from 'lucide-react';

import { fmtMoney, fmtMonth } from '../model';
import type { DealStage, ForecastReport } from '../types';
import { stageStyle } from './parts';

type Mode = 'etapa' | 'mes';

export function ForecastPanel({ report, stages, currency, loading, error, onRetry }: {
  report: ForecastReport | null;
  stages: DealStage[];
  currency: string;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  const [mode, setMode] = useState<Mode>('etapa');

  if (error) {
    return (
      <div className="p-4">
        <p role="alert" className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-[13px] text-destructive">
          No se pudo calcular el forecast: {error}{' '}
          <button type="button" onClick={onRetry} className="font-semibold underline">Reintentar</button>
        </p>
      </div>
    );
  }

  const tiles = [
    { icon: <Layers className="h-5 w-5" />, tone: 'violet', label: 'Deals abiertos', value: report ? report.open_count.toLocaleString('es-MX') : null, sub: 'en etapas abiertas' },
    { icon: <TrendingUp className="h-5 w-5" />, tone: 'indigo', label: 'Pipeline total', value: report ? fmtMoney(report.total_pipeline, currency) : null, sub: 'valor de lo abierto' },
    { icon: <Scale className="h-5 w-5" />, tone: 'magenta', label: 'Pipeline ponderado', value: report ? fmtMoney(report.weighted_pipeline, currency) : null, sub: 'valor × probabilidad' },
  ];

  const stageById = new Map(stages.map((s) => [s.id, s]));
  const rows = !report ? [] : mode === 'etapa'
    ? report.by_stage.map((b) => ({ key: String(b.stage_id), label: b.stage_name, stage: stageById.get(b.stage_id), count: b.open_count, total: Number(b.total_value), weighted: Number(b.weighted_value) }))
    : report.by_close_month.map((b) => ({ key: b.month, label: b.month ? fmtMonth(b.month) : 'Sin fecha de cierre', stage: undefined, count: b.open_count, total: Number(b.total_value), weighted: Number(b.weighted_value) }));
  const max = rows.reduce((m, r) => Math.max(m, r.total, r.weighted), 0);

  return (
    <div className="space-y-4 p-3 sm:p-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {tiles.map((t, i) => (
          <div key={t.label} className={`orion-card min-w-0 rounded-[20px] border border-border bg-card p-3.5 sm:p-5 ${i === 0 ? 'col-span-2 sm:col-span-1' : ''}`}>
            <div className="flex items-center gap-2.5">
              <span className={`orion-tone tone-${t.tone} grid h-9 w-9 shrink-0 place-items-center rounded-xl sm:h-10 sm:w-10`} aria-hidden>{t.icon}</span>
              <span className="text-sm font-semibold leading-tight text-foreground">{t.label}</span>
            </div>
            <div className="mt-3 truncate text-[20px] font-semibold leading-none tracking-tight tabular-nums text-foreground min-[420px]:text-[22px] sm:text-[26px]">
              {loading && !report ? <span className="inline-block h-7 w-24 animate-pulse rounded-md bg-muted align-middle" aria-label="Cargando" /> : t.value ?? '—'}
            </div>
            <div className="mt-1.5 text-xs text-muted-foreground">{t.sub}</div>
          </div>
        ))}
      </div>

      <section className="orion-card rounded-[20px] border border-border bg-card p-4 sm:p-5" aria-labelledby="dl-fc-title">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div className="min-w-0 flex-1">
            <h2 id="dl-fc-title" className="flex items-center gap-2 text-base font-semibold text-foreground">
              {mode === 'etapa' ? <Target className="h-4 w-4 dl-accent-text" aria-hidden /> : <CalendarRange className="h-4 w-4 dl-accent-text" aria-hidden />}
              {mode === 'etapa' ? 'Por etapa' : 'Por mes de cierre esperado'}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Solo deals abiertos. Barra clara = valor total; barra sólida = ponderado por probabilidad.
            </p>
          </div>
          <div role="tablist" aria-label="Agrupar forecast" className="grid grid-cols-2 gap-1 rounded-xl bg-muted p-1 sm:w-64">
            {([['etapa', 'Por etapa'], ['mes', 'Por mes']] as const).map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => setMode(k)}
                className={`min-h-[36px] rounded-[10px] px-2 text-[13px] font-medium ${mode === k ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{l}</button>
            ))}
          </div>
        </div>

        {loading && !report ? (
          <p className="mt-4 text-[13px] text-muted-foreground" role="status">Calculando forecast…</p>
        ) : rows.length === 0 ? (
          <p className="mt-4 rounded-xl border border-dashed border-border px-4 py-6 text-center text-[13px] text-muted-foreground">
            {mode === 'etapa'
              ? 'Aún no hay deals abiertos. El forecast se llena en cuanto haya deals en etapas abiertas.'
              : 'Ningún deal abierto tiene fecha de cierre esperada. Agrégala en la ficha del deal.'}
          </p>
        ) : (
          <ol className="mt-4 space-y-3.5">
            {rows.map((r) => {
              const wt = max ? Math.max(r.total ? 1.5 : 0, (r.total / max) * 100) : 0;
              const ww = max ? Math.max(r.weighted ? 1.5 : 0, (r.weighted / max) * 100) : 0;
              return (
                <li key={r.key} style={stageStyle(r.stage)}>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
                    <span className="flex min-w-0 flex-1 items-center gap-2 text-foreground">
                      {mode === 'etapa' && <span className="dl-dot h-2.5 w-2.5 shrink-0 rounded-full" aria-hidden />}
                      <span className="truncate font-medium">{r.label}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{r.count} {r.count === 1 ? 'deal' : 'deals'}</span>
                    </span>
                    <span className="shrink-0 tabular-nums">
                      <span className="font-semibold text-foreground">{fmtMoney(r.weighted, currency)}</span>
                      <span className="ml-2 text-xs text-muted-foreground">de {fmtMoney(r.total, currency)}</span>
                    </span>
                  </div>
                  <div className="relative mt-1.5 h-2.5 overflow-hidden rounded-full bg-muted"
                    title={`${r.label}: ponderado ${fmtMoney(r.weighted, currency)} de ${fmtMoney(r.total, currency)}`}>
                    <div className={`dl-grow absolute inset-y-0 left-0 rounded-full opacity-30 ${mode === 'etapa' ? 'dl-bar' : 'bg-primary'}`} style={{ width: `${wt}%` }} />
                    <div className={`dl-grow absolute inset-y-0 left-0 rounded-full ${mode === 'etapa' ? 'dl-bar' : 'bg-primary'}`} style={{ width: `${ww}%` }} />
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        {report?.generated_at && (
          <p className="mt-4 text-[11.5px] text-muted-foreground">Calculado por el servidor · {new Date(report.generated_at).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Mexico_City' })}</p>
        )}
      </section>
    </div>
  );
}
